import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { MockApiService } from '@zopup/zop-core';
import { WorkOrderDto, WorkPlanDto } from '@zopup/zop-types';
import { generateMockWorkPlans } from './data';

// The real WorkPlansService calls an absolute URL (`${MANUFACTURING_API_BASE_PATH}/WorkPlans/...`).
// `:scheme` / `:host` are wildcard segments (MockApiService treats any `:`-prefixed
// segment as "match anything") so these handlers work regardless of which environment
// (local gateway, Azure dev container, etc.) resolves that base path.
const workPlansUrl = (path = ''): string => `:scheme//:host/WorkPlans${path}`;

@Injectable({ providedIn: 'root' })
export class WorkPlansMockApi {
    private _workPlans: WorkPlanDto[] = generateMockWorkPlans();

    constructor(private _zopMockApiService: MockApiService) {
        this.registerHandlers();
    }

    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ WorkPlans - GET (paged list) - registered before the `:id` handler so the literal
        // "GetPagedList" and "Trigger" segments are matched first
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onGet(workPlansUrl('/GetPagedList')).reply(({ request }) => {
            const searchText = (request.params.get('SearchText') || '').toLowerCase();
            const subjectType = request.params.get('SubjectType') || '';
            const subjectId = request.params.get('SubjectId') || '';
            const triggerType = request.params.get('TriggerType') || '';
            const activeParam = request.params.get('Active');
            const pageNumber = Number(request.params.get('PageNumber')) || 1;
            const pageSize = Number(request.params.get('PageSize')) || 50;
            const orderBy = request.params.getAll('OrderBy') || [];
            const isDescending = request.params.get('IsOrderByDescending') === 'true';

            let rows = cloneDeep(this._workPlans);

            if (subjectType) {
                rows = rows.filter((wp) => wp.subjectType === subjectType);
            }

            if (subjectId) {
                rows = rows.filter((wp) => wp.subjectId === subjectId);
            }

            if (triggerType) {
                rows = rows.filter((wp) => wp.trigger?.type === triggerType);
            }

            if (activeParam !== null && activeParam !== '') {
                const active = activeParam === 'true';
                rows = rows.filter((wp) => !!wp.active === active);
            }

            if (searchText) {
                rows = rows.filter(
                    (wp) =>
                        wp.code?.toLowerCase().includes(searchText) ||
                        wp.name?.toLowerCase().includes(searchText) ||
                        wp.subjectId?.toLowerCase().includes(searchText)
                );
            }

            if (orderBy.length) {
                const [field] = orderBy;
                rows.sort((a, b) => {
                    const aValue = String((a as any)[field] ?? '');
                    const bValue = String((b as any)[field] ?? '');
                    return isDescending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
                });
            }

            const totalCount = rows.length;
            const start = (pageNumber - 1) * pageSize;
            const paged = rows.slice(start, start + pageSize);

            return [200, { isSuccess: true, result: paged, totalCount }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkPlans - PUT Trigger
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onPut(workPlansUrl('/Trigger')).reply(({ request }) => {
            const body = request.body as { id?: string; version?: number; currentReading?: number } | undefined;
            const workPlan = this._workPlans.find((wp) => wp.id === body?.id);
            if (!workPlan) {
                return [200, { isSuccess: false, errors: [{ message: 'Work plan not found' }] }];
            }

            const now = new Date();
            workPlan.version = (workPlan.version ?? 1) + 1;
            workPlan.scheduleState = {
                ...workPlan.scheduleState,
                lastTriggeredAt: now,
                lastGeneratedWorkOrderId: `wo-${workPlan.code?.toLowerCase()}-${Date.now()}`,
            };

            if (workPlan.trigger?.type === 'Meter' && body?.currentReading !== undefined) {
                workPlan.scheduleState.lastTriggerReading = body.currentReading;
                workPlan.scheduleState.nextDueReading =
                    body.currentReading + (workPlan.trigger.intervalValue ?? 0);
            } else if (workPlan.trigger?.type === 'Calendar') {
                const next = new Date(now);
                next.setDate(next.getDate() + (workPlan.trigger.intervalValue ?? 30));
                workPlan.scheduleState.nextDueAt = next;
            }

            const generatedWorkOrder: WorkOrderDto = {
                id: workPlan.scheduleState.lastGeneratedWorkOrderId,
                number: `WO-${Date.now()}`,
                type: 'Maintenance',
                subjectType: workPlan.subjectType,
                subjectId: workPlan.subjectId,
                targetQuantity: { value: 1, unit: 'EA' },
                scope: `Generated from work plan ${workPlan.code}`,
                status: 'Draft',
                workDefinitionId: workPlan.workDefinitionId,
                version: 1,
            };

            return [200, { isSuccess: true, result: generatedWorkOrder }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkPlans - GET by id
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onGet(workPlansUrl('/:id')).reply(({ urlParams }) => {
            const workPlan = this._workPlans.find((wp) => wp.id === urlParams['id']);
            if (!workPlan) {
                return [200, { isSuccess: false, errors: [{ message: 'Work plan not found' }] }];
            }
            return [200, { isSuccess: true, result: cloneDeep(workPlan) }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkPlans - POST (create)
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onPost(workPlansUrl()).reply(({ request }) => {
            const workPlanInput = request.body?.workPlan ?? {};
            const newWorkPlan: WorkPlanDto = {
                id: `wp-${this._workPlans.length + 1}-${Date.now()}`,
                active: true,
                scheduleState: {},
                version: 1,
                ...workPlanInput,
            };
            this._workPlans = [newWorkPlan, ...this._workPlans];
            return [200, { isSuccess: true, result: cloneDeep(newWorkPlan) }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkPlans - PUT (update)
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onPut(workPlansUrl()).reply(({ request }) => {
            const body = request.body as { id?: string; version?: number; workPlan?: Partial<WorkPlanDto> } | undefined;
            const workPlan = this._workPlans.find((wp) => wp.id === body?.id);
            if (!workPlan) {
                return [200, { isSuccess: false, errors: [{ message: 'Work plan not found' }] }];
            }

            Object.assign(workPlan, body?.workPlan ?? {});
            workPlan.version = (workPlan.version ?? 1) + 1;

            return [200, { isSuccess: true, result: cloneDeep(workPlan) }];
        });
    }
}
